import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { http, formatApiError } from "@/lib/api";
import { ArrowRight, Check, Play } from "lucide-react";
import { toast } from "sonner";
import Testimonials from "@/components/Testimonials";
import FAQs from "@/components/FAQs";

const HERO_BG = "https://images.unsplash.com/photo-1489599735906-2b02b16b1c3d?crop=entropy&cs=srgb&fm=jpg&w=1800";

export default function EditBaeHome() {
  const [services, setServices] = useState([]);
  const [cases, setCases] = useState([]);
  const [settings, setSettings] = useState({});

  useEffect(() => {
    http.get("/services").then(({ data }) => setServices(data.items));
    http.get("/case-studies").then(({ data }) => setCases(data.items));
    http.get("/settings").then(({ data }) => setSettings(data));
  }, []);

  return (
    <div data-testid="editbae-home">
      {/* HERO */}
      <section className="relative overflow-hidden pt-16 pb-24 lg:pt-24 lg:pb-32">
        <div className="absolute inset-0 -z-10 opacity-15" style={{ backgroundImage: `url(${HERO_BG})`, backgroundSize: "cover", backgroundPosition: "center" }} />
        <div className="absolute inset-0 -z-10" style={{ background: "linear-gradient(180deg, rgba(5,5,5,0.7), rgba(5,5,5,0.98))" }} />
        <div className="absolute -bottom-40 -left-20 w-[520px] h-[520px] rounded-full blur-3xl opacity-25 float-slow" style={{ background: "#A855F7" }} />

        <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
          <motion.div
            initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8 }}
            className="mb-10"
          >
            <div className="text-[11px] tracking-[0.32em] uppercase text-gray-400 mb-6">
              The Creative Studio of AI Creator OS
            </div>
            <div className="font-display-media italic text-6xl sm:text-7xl lg:text-8xl font-black tracking-tight leading-[0.9]">
              Edit Bae <span className="text-[#A855F7]">Studios</span>
            </div>
          </motion.div>

          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-end mt-14">
            <motion.h1
              initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.15, duration: 0.9 }}
              className="lg:col-span-8 text-4xl sm:text-5xl lg:text-6xl font-black tracking-tight leading-[1.02] font-display-media italic"
            >
              Cinematic AI content, <span className="text-[#A855F7]">built for brands</span> that refuse to blend in.
            </motion.h1>
            <motion.p
              initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.25, duration: 0.9 }}
              className="lg:col-span-4 text-gray-300 text-lg leading-relaxed"
            >
              We create cinematic AI commercials, luxury brand campaigns, premium content systems, and high-converting creative assets for modern businesses.
            </motion.p>
          </div>

          <div className="mt-12 flex flex-wrap gap-3">
            <a href="#book" data-testid="editbae-cta-book" className="btn-purple px-7 py-4 rounded-full text-sm inline-flex items-center gap-2">
              Book Strategy Call <ArrowRight size={16} />
            </a>
            <a href="#work" data-testid="editbae-cta-portfolio" className="px-7 py-4 rounded-full border hairline text-sm hover:bg-white/5 inline-flex items-center gap-2">
              View Portfolio
            </a>
          </div>
        </div>
      </section>

      {/* SERVICES */}
      <section id="services" className="max-w-[1400px] mx-auto px-6 lg:px-10 py-24">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 mb-14">
          <div className="lg:col-span-7">
            <div className="label-eyebrow mb-3">What we make</div>
            <h2 className="text-4xl sm:text-5xl font-black tracking-tight font-display-media italic leading-tight">
              Six disciplines. <span className="text-[#A855F7]">One creative bench.</span>
            </h2>
          </div>
          <p className="lg:col-span-5 text-gray-400 self-end leading-relaxed">
            Every engagement runs through the same edit bay. That's how we keep the visual language consistent across an entire campaign — not just a single asset.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {services.map((s) => (
            <div key={s.slug} className="glass rounded-2xl p-7 hover:-translate-y-1 transition group" data-testid={`service-${s.slug}`}>
              <div className="text-xs tracking-[0.28em] uppercase text-[#A855F7] mb-4">{s.title.split(" ")[0]}</div>
              <div className="text-2xl font-semibold font-display-media italic mb-3">{s.title}</div>
              <div className="text-sm text-gray-400 leading-relaxed">{s.body || s.summary}</div>
            </div>
          ))}
        </div>
      </section>

      {/* CASE STUDIES */}
      <section id="work" className="max-w-[1400px] mx-auto px-6 lg:px-10 py-16">
        <div className="flex items-end justify-between mb-10 flex-wrap gap-4">
          <div>
            <div className="label-eyebrow mb-3">Featured Campaigns</div>
            <h3 className="text-4xl sm:text-5xl font-black tracking-tight font-display-media italic">
              Selected work.
            </h3>
          </div>
          <div className="text-sm text-gray-400">2024 — 2025</div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
          {cases.map((c, i) => (
            <Link
              to={`/studio/case/${c.slug}`}
              key={c.slug}
              data-testid={`case-${c.slug}`}
              className={`group relative rounded-2xl overflow-hidden border hairline block ${i === 0 ? "md:col-span-2" : ""}`}
            >
              <div className={`overflow-hidden ${i === 0 ? "aspect-[21/9]" : "aspect-[4/5]"}`}>
                <img
                  src={c.hero_image}
                  alt={c.client}
                  className="w-full h-full object-cover transition duration-700 group-hover:scale-[1.06]"
                />
              </div>
              <div className="absolute inset-0 bg-gradient-to-t from-black via-black/40 to-transparent" />
              <div className="absolute inset-x-0 bottom-0 p-7">
                <div className="flex items-center gap-2 text-[10px] tracking-[0.28em] uppercase text-gray-300 mb-2">
                  <span className="text-[#A855F7]">{c.tag}</span>
                  <span>·</span>
                  <span>{c.year}</span>
                </div>
                <div className="text-2xl sm:text-3xl font-black font-display-media italic leading-tight">{c.client}</div>
                <div className="text-sm text-gray-300 mt-1">{c.project}</div>
              </div>
              <div className="absolute top-5 right-5 glass rounded-full w-11 h-11 grid place-items-center opacity-0 group-hover:opacity-100 transition">
                <Play size={16} className="text-white ml-0.5" />
              </div>
            </Link>
          ))}
        </div>
      </section>

      {/* TESTIMONIALS */}
      <Testimonials accent="#A855F7" title="What clients are saying" />

      {/* FAQ */}
      <FAQs category="studio" accent="#A855F7" title="Studio questions answered" />

      {/* BOOK / START A PROJECT */}
      <section id="book" className="max-w-[1400px] mx-auto px-6 lg:px-10 py-24">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-start">
          <div className="lg:col-span-5">
            <div className="label-eyebrow mb-3">Start Your Project</div>
            <h3 className="text-4xl sm:text-6xl font-black tracking-tight font-display-media italic leading-[0.95]">
              Let's build something{" "}
              <span className="text-[#A855F7]">worth remembering.</span>
            </h3>
            <p className="text-gray-400 mt-6 leading-relaxed">
              Every project starts with strategy. Book a complimentary discovery call where we'll discuss your goals, your brand, and how cinematic AI content can help you stand out.
            </p>
            <ul className="mt-8 space-y-2 text-sm text-gray-300">
              {["Senior creative direction, no junior pass-through", "Selective engagements — quality over volume", "Response within one business day"].map((l) => (
                <li key={l} className="flex items-start gap-2"><Check size={16} className="text-[#A855F7] mt-0.5" />{l}</li>
              ))}
            </ul>
            <div className="mt-8 text-sm text-gray-500">
              For larger project inquiries: <a href={`mailto:${settings.editbae_email || "studio@theaicreatoros.com"}`} className="text-[#A855F7] hover:underline">{settings.editbae_email || "studio@theaicreatoros.com"}</a>
            </div>
          </div>

          <div className="lg:col-span-7">
            <CalendlyEmbed url={settings.calendly_url} />
            <ProjectInquiryForm />
          </div>
        </div>
      </section>
    </div>
  );
}

function CalendlyEmbed({ url }) {
  if (!url) {
    return (
      <div className="glass rounded-2xl p-8 mb-6" data-testid="calendly-placeholder">
        <div className="label-eyebrow mb-3">Discovery Call</div>
        <div className="text-xl font-semibold font-display-media italic mb-2">Booking coming online shortly.</div>
        <p className="text-sm text-gray-400 leading-relaxed">
          In the meantime, use the inquiry form below and we'll respond within one business day.
        </p>
      </div>
    );
  }
  return (
    <div className="glass rounded-2xl overflow-hidden mb-6" data-testid="calendly-embed">
      <iframe
        src={url}
        title="Book a discovery call"
        style={{ width: "100%", height: "720px", border: "none" }}
        allow="clipboard-write"
      />
    </div>
  );
}

function ProjectInquiryForm() {
  const [form, setForm] = useState({ name: "", email: "", company: "", message: "", kind: "booking" });
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await http.post("/leads/contact", form);
      setSent(true);
      toast.success("Message received. We'll be in touch shortly.");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Failed");
    } finally {
      setBusy(false);
    }
  };

  if (sent) {
    return (
      <div className="glass rounded-2xl p-8 text-center" data-testid="editbae-inquiry-sent">
        <div className="text-2xl font-semibold font-display-media italic mb-2">Message received.</div>
        <p className="text-gray-400">We respond personally within one business day.</p>
      </div>
    );
  }

  return (
    <form onSubmit={submit} className="glass rounded-2xl p-7" data-testid="editbae-inquiry-form">
      <div className="label-eyebrow mb-4">Larger Project Inquiry</div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Field label="Name" value={form.name} onChange={(v) => setForm({ ...form, name: v })} testid="editbae-contact-name" />
        <Field label="Email" type="email" value={form.email} onChange={(v) => setForm({ ...form, email: v })} testid="editbae-contact-email" />
        <Field label="Company" value={form.company} onChange={(v) => setForm({ ...form, company: v })} testid="editbae-contact-company" wrap="md:col-span-2" />
      </div>
      <div className="mt-4">
        <label className="text-xs text-gray-400 mb-1.5 block">Project brief</label>
        <textarea
          required
          rows={4}
          value={form.message}
          onChange={(e) => setForm({ ...form, message: e.target.value })}
          data-testid="editbae-contact-message"
          className="w-full bg-black/40 border hairline rounded-md px-4 py-3 text-sm focus:outline-none focus:border-[#A855F7]/60"
        />
      </div>
      <button disabled={busy} data-testid="editbae-contact-submit" className="mt-5 btn-purple px-6 py-3.5 rounded-full text-sm inline-flex items-center gap-2">
        {busy ? "Sending…" : "Send inquiry"} <ArrowRight size={16} />
      </button>
    </form>
  );
}

function Field({ label, type = "text", value, onChange, testid, wrap = "" }) {
  return (
    <div className={wrap}>
      <label className="text-xs text-gray-400 mb-1.5 block">{label}</label>
      <input
        type={type}
        required={label !== "Company"}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        data-testid={testid}
        className="w-full bg-black/40 border hairline rounded-md px-4 py-3 text-sm focus:outline-none focus:border-[#A855F7]/60"
      />
    </div>
  );
}
